import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";

import { HelpHint } from "@/components/help";
import { HELP_KEYS } from "@/lib/help-context";
import { useUser } from "@/auth/session";
import { RestoreSiteButton } from "@/components/sites/RestoreSiteButton";
import { PendingDeletionNotice } from "@/components/sites/SiteDeletion";
import { DeletionCountdown } from "@/components/status";
import {
  Button,
  CopyLinkButton,
  ErrorAlert,
  ErrorState,
  FullPageSpinner,
  LinkButton,
  Select,
  Badge,
  Section,
  KeyValue,
  buttonClass,
  useToast,
} from "@/components/ui";
import { portal, runtime } from "@/lib/api";
import { formatActivityTime, formatClock, formatDate, formatDayMonth } from "@/lib/format";
import { isLive, storageUsed, useHosting, type HostedSite } from "@/lib/hosting";
import { primarySite, useOverview } from "@/lib/queries";
import { mb } from "@/lib/static-site";
import type { CustomDomain, Plan } from "@/lib/types";
import { choice, useUrlView, type SetView, type View } from "@/lib/url-state";
import { HostingHead, CardHead, UsageMeter, textLink } from "@/components/hosting/parts";

const SITE_SORTS = [
  ["name", "Name A–Z"],
  ["status", "Status"],
  ["newest", "Newest first"],
] as const;
const DOMAIN_SORTS = [
  ["domain", "Domain A–Z"],
  ["connection", "Connection"],
] as const;
/**
 * The Hosting lists' sort, kept in the URL (PHP-3341). Each list has its own param:
 * - External sites, `?sites_sort=`: name A–Z (the default), status (problems first: failed, DNS not
 *   found, deploying, awaiting DNS, not live, live) or newest first (when the site was added).
 * - Domains & security, `?domains_sort=`: domain A–Z (the default) or connection (DNS not found, then
 *   awaiting DNS, then connected).
 * Ties fall back to name or domain A–Z.
 */
const VIEW = {
  sites: choice(
    "sites_sort",
    SITE_SORTS.map(([s]) => s),
    "name",
  ),
  domains: choice(
    "domains_sort",
    DOMAIN_SORTS.map(([s]) => s),
    "domain",
  ),
};
type HostingView = View<typeof VIEW>;
type SetHostingView = SetView<typeof VIEW>;
const byText = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: "base" });

function SortSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly (readonly [string, string])[];
  onChange: (value: string) => void;
}) {
  return (
    <Select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 w-auto rounded-full py-1 text-[12.5px]"
    >
      {options.map(([v, text]) => (
        <option key={v} value={v}>
          {text}
        </option>
      ))}
    </Select>
  );
}

/** Figma "Hosting — Overview · Weavex site live" (PRT-011, HST-01). */
export function HostingPage() {
  const overview = useOverview();
  const hosting = useHosting();
  const [view, setView] = useUrlView(VIEW);

  if (overview.isPending || hosting.isPending) return <FullPageSpinner />;
  if (overview.isError) return <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />;
  if (hosting.isError) return <ErrorState error={hosting.error} onRetry={() => void hosting.refetch()} />;

  const sites = hosting.data;
  const plan = overview.data.account.plan;
  const mainSite = primarySite(sites.map((h) => h.site));
  const main = sites.find((h) => h.site.id === mainSite?.id);
  const externals = sites.filter((h) => h.site.kind === "external");
  const live = main && isLive(main.site);
  const address = main
    ? (main.domains.find((d) => d.status === "active")?.hostname ?? main.site.hostname)
    : null;

  return (
    <div className="flex flex-col gap-6">
      <HostingHead
        title="Hosting"
        status={live ? <Badge tone="success">Live</Badge> : <Badge tone="warning">Not live yet</Badge>}
        subtitle={
          address && live
            ? `${address} runs on CortexGrip hosting · built with Weavex`
            : "Your Weavex website and any site you built elsewhere, hosted by CortexGrip"
        }
        actions={
          <>
            <CopyLinkButton className="py-[9px] text-[13px]" />
            <LinkButton
              to="/hosting/external/new"
              requires="websites:create"
              className={buttonClass("secondary", "pill")}
            >
              Add an external site
            </LinkButton>
            {main && live && (
              <a
                href={main.site.url}
                target="_blank"
                rel="noreferrer"
                className={buttonClass("brand", "pill")}
              >
                Open live site ↗
              </a>
            )}
          </>
        }
      />

      {main?.site.status === "pending_deletion" && <PendingDeletionNotice site={main.site} />}

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <CurrentDeployment hosted={main} />
          {externals.length > 0 && <ExternalSites sites={externals} view={view} setView={setView} />}
          <DomainsAndSecurity sites={sites} view={view} setView={setView} />
        </div>
        <div className="flex w-full shrink-0 flex-col gap-4 lg:w-[360px]">
          <HostingStatus externals={externals.length} />
          <PlanAndUsage plan={plan} sites={sites} subdomain={main?.site.hostname} />
        </div>
      </div>
    </div>
  );
}

function CurrentDeployment({ hosted }: { hosted: HostedSite | undefined }) {
  const user = useUser();
  const qc = useQueryClient();
  const toast = useToast();
  const promote = useMutation({
    mutationFn: (deploymentId: string) => portal.promote(hosted?.site.id ?? "", deploymentId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["hosting"] });
      toast.show({ tone: "success", title: "Version set live" });
    },
  });

  if (!hosted) {
    return (
      <Section variant="tight">
        <CardHead
          icon="zap"
          title="Current deployment"
          aside={<Badge tone="neutral">Nothing published</Badge>}
        />
        <p className="text-[13px] text-fg-subtle">
          Your Weavex website appears here once it is published.{" "}
          <Link to="/websites" className={textLink}>
            Go to Website →
          </Link>
        </p>
      </Section>
    );
  }

  const { site, deployments } = hosted;
  const current = deployments.find((d) => d.id === site.live_deployment_id);
  const versions = deployments.filter((d) => d.status === "ready").slice(0, 3);
  const by = current?.created_by_user_id === user.id ? " by you" : "";

  return (
    <Section variant="tight">
      <CardHead
        icon="zap"
        title="Current deployment"
        aside={isLive(site) ? <Badge tone="success">Live</Badge> : <Badge tone="warning">Not live</Badge>}
      />
      {current ? (
        <>
          <KeyValue variant="compact" label="Version">
            v{current.number} · Weavex site
          </KeyValue>
          <KeyValue variant="compact" label="Published">
            {formatActivityTime(current.finished_at ?? current.created_at)}
            {by}
          </KeyValue>
          <KeyValue variant="compact" label="Deployment reference">
            {current.id.slice(0, 8)}
          </KeyValue>
        </>
      ) : (
        <p className="text-[13px] text-fg-subtle">Nothing is live yet. Publish from the Website page.</p>
      )}
      {versions.length > 0 && (
        <>
          <div className="h-px bg-border" />
          <p className="text-[11.5px] font-semibold tracking-[0.575px] text-fg-subtle uppercase">
            Recent versions
          </p>
          {versions.map((d) => {
            const isCurrent = d.id === site.live_deployment_id;
            return (
              <div key={d.id} className="flex items-center gap-3">
                <span
                  className={
                    isCurrent
                      ? "rounded-md bg-[#d9f0e6] px-[7px] py-0.5 text-[11.5px] font-bold text-[#13734d]"
                      : "rounded-md bg-muted px-[7px] py-0.5 text-[11.5px] font-bold text-fg-muted"
                  }
                >
                  v{d.number}
                </span>
                <span className="min-w-0 flex-1 truncate text-[13px] text-fg">
                  {d.file_count.toLocaleString()} files · {formatDayMonth(d.finished_at ?? d.created_at)}
                </span>
                {isCurrent ? (
                  <span className="text-[12.5px] font-semibold text-[#13734d]">Live now</span>
                ) : (
                  <Button
                    unstyled
                    className={textLink}
                    requires={{ permission: "deployments:manage", on: ["site", hosted?.site.id] }}
                    loading={promote.isPending}
                    onClick={() => promote.mutate(d.id)}
                  >
                    {promote.isPending && promote.variables === d.id ? "Restoring…" : "Restore →"}
                  </Button>
                )}
              </div>
            );
          })}
          {promote.error && <ErrorAlert error={promote.error} />}
          <p className="text-xs leading-normal text-fg-subtle">
            Restoring a version redeploys it. Your current version stays live until the restore finishes.
          </p>
        </>
      )}
    </Section>
  );
}

/** An external site's state, problems first (the order the status sort uses). */
const SITE_STATES = [
  ["failed", "danger", "Failed"],
  ["dns_failed", "danger", "DNS not found"],
  ["pending_deletion", "warning", "Scheduled for deletion"],
  ["deploying", "warning", "Deploying"],
  ["awaiting_dns", "warning", "Awaiting DNS"],
  ["not_live", "warning", "Not live yet"],
  ["live", "success", "Live"],
] as const;

function siteState({ site, deployments, domains }: HostedSite): number {
  const latest = deployments[0];
  const state =
    site.status === "pending_deletion"
      ? "pending_deletion"
      : latest?.status === "failed"
        ? "failed"
        : latest && latest.status !== "ready"
          ? "deploying"
          : domains.some((d) => d.status === "failed")
            ? "dns_failed"
            : domains.some((d) => d.status === "pending_verification")
              ? "awaiting_dns"
              : isLive(site)
                ? "live"
                : "not_live";
  return SITE_STATES.findIndex(([key]) => key === state);
}

function siteStatusPill(h: HostedSite) {
  const [, tone, label] = SITE_STATES[siteState(h)]!;
  return <Badge tone={tone}>{label}</Badge>;
}

function sortSites(sites: HostedSite[], sort: HostingView["sites"]): HostedSite[] {
  const byName = (a: HostedSite, b: HostedSite) => byText(a.site.name, b.site.name);
  if (sort === "status") return [...sites].sort((a, b) => siteState(a) - siteState(b) || byName(a, b));
  if (sort === "newest")
    return [...sites].sort(
      (a, b) => (b.site.created_at ?? "").localeCompare(a.site.created_at ?? "") || byName(a, b),
    );
  return [...sites].sort(byName);
}

function ExternalSites({
  sites,
  view,
  setView,
}: {
  sites: HostedSite[];
  view: HostingView;
  setView: SetHostingView;
}) {
  return (
    <Section variant="tight">
      <CardHead
        icon="server"
        title={
          <>
            External sites <HelpHint helpKey={HELP_KEYS.hosting.externalSites} label="external sites" />
          </>
        }
        aside={
          <span className="flex items-center gap-2">
            {sites.length > 1 && (
              <SortSelect
                label="Sort external sites"
                value={view.sites}
                options={SITE_SORTS}
                onChange={(v) => setView({ sites: v as HostingView["sites"] })}
              />
            )}
            <span className="text-[12.5px] text-fg-subtle">Static</span>
          </span>
        }
      />
      {sortSites(sites, view.sites).map((h, i) => (
        <div key={h.site.id} data-testid={`external-site-${h.site.id}`} className="flex flex-col gap-3.5">
          {i > 0 && <div className="h-px bg-border" />}
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={`/hosting/sites/${h.site.id}`}
              className="group flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-2"
            >
              <div className="flex min-w-[9rem] flex-1 flex-col gap-0.5">
                <span className="truncate text-[13.5px] font-semibold text-fg group-hover:underline">
                  {h.site.name}
                </span>
                <span className="truncate text-[11.5px] text-fg-subtle">
                  {h.domains[0]?.hostname ?? h.site.hostname}
                </span>
              </div>
              {siteStatusPill(h)}
              <DeletionCountdown site={h.site} />
              <span className={textLink}>→</span>
            </Link>
            {h.site.status === "pending_deletion" && <RestoreSiteButton site={h.site} />}
          </div>
        </div>
      ))}
    </Section>
  );
}

function connection(d: CustomDomain) {
  if (d.status === "active") return <Badge tone="success">Connected</Badge>;
  if (d.status === "failed") return <Badge tone="danger">DNS not found</Badge>;
  return <Badge tone="warning">Awaiting DNS</Badge>;
}

function certificate(d: CustomDomain) {
  if (d.status === "active") return <Badge tone="success">Active</Badge>;
  return <Badge tone="neutral">Waiting for DNS</Badge>;
}

/** Connection problems first, the order the connection sort uses. */
const CONNECTION_ORDER: Record<CustomDomain["status"], number> = {
  failed: 0,
  pending_verification: 1,
  unknown: 1,
  active: 2,
};

function DomainsAndSecurity({
  sites,
  view,
  setView,
}: {
  sites: HostedSite[];
  view: HostingView;
  setView: SetHostingView;
}) {
  const rows = sites
    .flatMap((h) => h.domains.map((domain) => ({ hosted: h, domain })))
    .sort(
      (a, b) =>
        (view.domains === "connection"
          ? CONNECTION_ORDER[a.domain.status] - CONNECTION_ORDER[b.domain.status]
          : 0) || byText(a.domain.hostname, b.domain.hostname),
    );
  return (
    <Section variant="tight">
      <CardHead
        icon="shield-tick"
        title="Domains & security (TLS)"
        aside={
          <span className="flex items-center gap-2">
            {rows.length > 1 && (
              <SortSelect
                label="Sort domains"
                value={view.domains}
                options={DOMAIN_SORTS}
                onChange={(v) => setView({ domains: v as HostingView["domains"] })}
              />
            )}
            <Link to="/domains" className={textLink}>
              Manage domains →
            </Link>
          </span>
        }
      />
      {rows.length === 0 ? (
        <p className="text-[13px] text-fg-subtle">
          No custom domains yet. Each site is served on its free CortexGrip address with a security
          certificate.
        </p>
      ) : (
        // Focusable so keyboard users can scroll the wide table (axe: scrollable-region-focusable).
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        <div className="overflow-x-auto" role="region" tabIndex={0} aria-label="Domains and security table">
          <div className="flex min-w-[520px] flex-col gap-3.5">
            <div className="flex text-[11px] font-semibold text-fg-subtle">
              <span className="flex-1">DOMAIN</span>
              <span className="w-[150px]">CONNECTION</span>
              <span className="w-[190px]">CERTIFICATE</span>
            </div>
            {rows.map(({ hosted, domain }) => {
              const to =
                hosted.site.kind === "external"
                  ? `/hosting/sites/${hosted.site.id}`
                  : `/websites/${hosted.site.id}?tab=domains`;
              return (
                <div key={domain.id} className="flex flex-col gap-3.5">
                  <div className="h-px bg-border" />
                  <div className="flex items-center">
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="truncate text-[13.5px] font-semibold text-fg">{domain.hostname}</span>
                      <span className="text-[11.5px] text-fg-subtle">
                        {domain.status === "active"
                          ? `Connected ${formatDate(domain.verified_at)}`
                          : "Issued once DNS is confirmed"}
                      </span>
                    </div>
                    <div className="w-[150px]">{connection(domain)}</div>
                    <div className="flex w-[190px] items-center">
                      <span className="flex-1">{certificate(domain)}</span>
                      {domain.status !== "active" && (
                        <Link to={to} className={textLink} aria-label={`Open ${domain.hostname}`}>
                          →
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Section>
  );
}

function HostingStatus({ externals }: { externals: number }) {
  const health = useQuery({
    queryKey: ["hosting-health"],
    queryFn: runtime.health,
    refetchInterval: 60_000,
    retry: false,
  });
  return (
    <Section variant="tight">
      <CardHead icon="refresh" title="Hosting status" />
      <KeyValue variant="compact" label="Service">
        {health.isPending ? (
          <Badge tone="neutral">Checking</Badge>
        ) : health.isSuccess ? (
          <Badge tone="success">Operating normally</Badge>
        ) : (
          <Badge tone="danger">Not responding</Badge>
        )}
      </KeyValue>
      <KeyValue variant="compact" label="Last health check">
        {health.dataUpdatedAt || health.errorUpdatedAt
          ? formatClock(Math.max(health.dataUpdatedAt, health.errorUpdatedAt))
          : "—"}
      </KeyValue>
      <KeyValue variant="compact" label="Site type">
        {externals ? `Weavex site + ${externals} external` : "Weavex site"}
      </KeyValue>
      <p className="text-xs leading-normal text-fg-subtle">
        If our hosting provider has a problem, we’ll show it here with the time we last confirmed your site.
      </p>
    </Section>
  );
}

function PlanAndUsage({ plan, sites, subdomain }: { plan: Plan; sites: HostedSite[]; subdomain?: string }) {
  const used = storageUsed(sites);
  return (
    <Section variant="tight">
      <CardHead
        icon="database"
        title="Plan & usage"
        aside={<span className="text-[12.5px] font-semibold text-fg-subtle">{plan.name}</span>}
      />
      <UsageMeter
        label="Storage"
        value={`${mb(used)} of ${mb(plan.storage_bytes)}`}
        fraction={used / plan.storage_bytes}
      />
      <p className="text-xs leading-normal text-fg-subtle">
        Built files only. The source you upload with each deployment counts toward your plan too.
      </p>
      <UsageMeter
        label="Sites"
        value={`${sites.length} of ${plan.max_sites}`}
        fraction={sites.length / plan.max_sites}
      />
      {subdomain && (
        <KeyValue variant="compact" label="Free subdomain">
          {subdomain}
        </KeyValue>
      )}
      <KeyValue variant="compact" label="Custom domains">
        {plan.max_custom_domains > 0 ? `${plan.max_custom_domains} included` : "Not included"}
      </KeyValue>
      <Link to="/billing/plans" className={textLink}>
        Compare plans →
      </Link>
    </Section>
  );
}
